"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Check, PlusCircle } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { OverlayCta } from "@/components/cta";
import { EffectArrow, Grade } from "@/components/edb";
import { LockBadgeOverlay } from "@/components/lock";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { useAccess } from "@/lib/hooks/use-access";
import { cn } from "@/lib/utils";
import { AccessRules } from "@/types";

export const effectOptions = [
	{
		text: (
			<span className="flex flex-col items-center justify-end">
				<EffectArrow arrow={{ icon: ["icon-up-3"], text: "Strong" }} />
				<EffectArrow arrow={{ icon: ["icon-down-3"], text: "Strong" }} />
				<small>Large</small>
			</span>
		),
		value: "large",
	},
	{
		text: (
			<span className="flex flex-col items-center justify-end">
				<EffectArrow arrow={{ icon: ["icon-up-2"], text: "Notable" }} />
				<EffectArrow arrow={{ icon: ["icon-down-2"], text: "Notable" }} />
				<small>Moderate</small>
			</span>
		),
		value: "moderate",
	},
	{
		text: (
			<span className="flex flex-col items-center justify-end">
				<EffectArrow arrow={{ icon: ["icon-up-1"], text: "Slight" }} />
				<EffectArrow arrow={{ icon: ["icon-down-1"], text: "Slight" }} />
				<small>Small</small>
			</span>
		),
		value: "small",
	},
	{
		text: (
			<span className="flex flex-col items-center justify-end gap-y-4">
				<EffectArrow arrow={{ icon: ["icon-up"], text: "Mixed" }} />
				<small>Mixed Effect</small>
			</span>
		),
		value: "mixed-effect",
	},
	{
		text: (
			<span className="flex flex-col items-center justify-end gap-y-4">
				<EffectArrow arrow={{ icon: [""], text: "No effect" }} />
				<small>No Effect</small>
			</span>
		),
		value: "no-effect",
	},
	{ text: "All", value: "all" },
];

export const gradeOptions = [
	{
		text: (
			<Grade
				entry={{ grade: "A", showContent: true }}
				className="size-9 leading-9 drop-shadow-none"
			/>
		),
		value: "a",
	},
	{
		text: (
			<Grade
				entry={{ grade: "B", showContent: true }}
				className="size-9 leading-9 drop-shadow-none"
			/>
		),
		value: "b",
	},
	{
		text: (
			<Grade
				entry={{ grade: "C", showContent: true }}
				className="size-9 leading-9 drop-shadow-none"
			/>
		),
		value: "c",
	},
	{
		text: (
			<Grade
				entry={{ grade: "D", showContent: true }}
				className="size-9 leading-9 drop-shadow-none"
			/>
		),
		value: "d",
	},
	{
		text: (
			<Grade
				entry={{ grade: "F", showContent: true }}
				className="size-9 leading-9 drop-shadow-none"
			/>
		),
		value: "f",
	},
	{ text: "All", value: "all" },
];

export const defaulFiltertValues = {
	grade: gradeOptions.map(({ value }) => value),
	effect: effectOptions.map(({ value }) => value),
	plusOnly: false,
};

const FormSchema = z.object({
	grade: z.array(z.string()).refine((value) => value.some((item) => item), {}),
	effect: z.array(z.string()).refine((value) => value.some((item) => item), {}),
	plusOnly: z.boolean(),
});

interface DatabaseFilterProps extends React.HTMLAttributes<HTMLDivElement> {
	filters?: z.infer<typeof FormSchema>;
	handleFilter: (data: z.infer<typeof FormSchema>) => void;
	accessRules?: AccessRules;
}

const DatabaseFilter = ({ children, accessRules, filters, handleFilter }: DatabaseFilterProps) => {
	const edbFilterRef = React.useRef<HTMLDivElement>(null);
	const closeRef = React.useRef(null);

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: filters,
	});
	// const { examine_database: hasAccess } = accessRules;
	const hasAccess = useAccess("examine_database");

	React.useEffect(() => {
		if (filters) {
			form.reset(filters);
		}
	}, [filters, form]);

	function onSubmit(data: z.infer<typeof FormSchema>) {
		handleFilter(data);
		closeRef.current.click();
	}

	return (
		<Sheet>
			<SheetTrigger asChild>{children}</SheetTrigger>
			<SheetContent
				side={"right"}
				className="overflow-x-hidden"
				onClick={(e) => e.stopPropagation()}
			>
				<SheetHeader className="flex flex-row items-center justify-between space-y-0">
					<SheetTitle>Examine Database Filter</SheetTitle>
					<SheetClose ref={closeRef} className="flex rotate-45 items-center">
						<PlusCircle />
					</SheetClose>
					<VisuallyHidden>
						<SheetDescription>Examine Database Filter</SheetDescription>
					</VisuallyHidden>
				</SheetHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="rounded-l-lg pb-4">
						<div className="flex justify-end">
							<Button
								onClick={(e) => {
									e.preventDefault();
									form.reset(defaulFiltertValues);
								}}
								variant="text"
								size="md"
								className="col-span-2"
							>
								Reset Filters
							</Button>
						</div>

						{hasAccess ? (
							<>
								<FormField
									control={form.control}
									name="grade"
									render={() => (
										<FormItem>
											<FormLabel className="mb-3.5 block px-0 py-1.5 font-lora text-lg font-semibold text-gray-900">
												Grade:
											</FormLabel>
											<div className="grid grid-cols-3 grid-rows-2 gap-3 sm:grid-cols-5 sm:grid-rows-1">
												{gradeOptions.map((option) => (
													<div
														className="flex items-stretch justify-start gap-2"
														key={option.value}
													>
														<FormField
															control={form.control}
															name="grade"
															render={({ field }) => {
																return (
																	<FormItem
																		key={option.value}
																		className="relative flex w-full min-w-fit flex-row items-center space-y-0"
																	>
																		<FormControl>
																			<Checkbox
																				className="peer absolute z-10 size-full cursor-pointer opacity-0"
																				checked={field.value?.includes(option.value)}
																				onCheckedChange={(checked) =>
																					option.value === "all"
																						? field.onChange(
																								checked
																									? [...gradeOptions.map(({ value }) => value)]
																									: []
																						  )
																						: field.onChange(
																								checked
																									? [...field.value, option.value]
																									: field.value
																											?.filter((value) => value !== option.value)
																											.filter((value) => value !== "all")
																						  )
																				}
																			/>
																		</FormControl>
																		<FormLabel
																			className={cn(
																				"flex size-full items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl border border-gray-400 p-2",
																				field.value?.includes(option.value)
																					? "bg-purple-100 ring-1 ring-primary"
																					: ""
																			)}
																		>
																			<Check size="16" />
																			{option.text}
																		</FormLabel>
																	</FormItem>
																);
															}}
														/>
													</div>
												))}
											</div>
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="effect"
									render={() => (
										<FormItem className="mt-4">
											<FormLabel className="mb-3.5 block px-0 py-1.5 font-lora text-lg font-semibold text-gray-900">
												Effect:
											</FormLabel>
											<div className="grid grid-cols-3 grid-rows-2 gap-3 sm:grid-cols-5 sm:grid-rows-1">
												{effectOptions.map((option) => (
													<div
														className="flex items-stretch justify-start gap-2"
														key={option.value}
													>
														<FormField
															control={form.control}
															name="effect"
															render={({ field }) => {
																return (
																	<FormItem
																		key={option.value}
																		className="relative flex w-full flex-row items-center space-y-0"
																	>
																		<FormControl>
																			<Checkbox
																				className="peer absolute z-10 size-full cursor-pointer opacity-0"
																				checked={field.value?.includes(option.value)}
																				onCheckedChange={(checked) =>
																					option.value === "all"
																						? field.onChange(
																								checked
																									? [...effectOptions.map(({ value }) => value)]
																									: []
																						  )
																						: field.onChange(
																								checked
																									? [...field.value, option.value]
																									: field.value
																											?.filter((value) => value !== option.value)
																											.filter((value) => value !== "all")
																						  )
																				}
																			/>
																		</FormControl>
																		<FormLabel
																			className={cn(
																				"flex size-full items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl border border-gray-400 p-2",
																				field.value?.includes(option.value)
																					? "bg-purple-100 ring-1 ring-primary"
																					: ""
																			)}
																		>
																			{option.text}
																		</FormLabel>
																	</FormItem>
																);
															}}
														/>
													</div>
												))}
											</div>
										</FormItem>
									)}
								/>

								<div className="mt-4 flex justify-end">
									<Button type="submit">Apply</Button>
								</div>
							</>
						) : (
							<div className="relative top-25" ref={edbFilterRef}>
								<LockBadgeOverlay
									size={10}
									className="w-3/4"
									text={<OverlayCta trackEdbSignup />}
								/>
							</div>
						)}
					</form>
				</Form>
			</SheetContent>
		</Sheet>
	);
};

export default DatabaseFilter;
